import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Trophy, Sparkles, TrendingUp } from "lucide-react";
import type { RtdPhase } from "@/lib/rtd-phases";

interface PhaseCelebrationProps {
  phase: RtdPhase | null;
  onDismiss: () => void;
  totalPhases: number;
}

const CONFETTI_COLORS = ["#f97316", "#fbbf24", "#a78bfa", "#4ecdc4", "#22d3ee", "#34d399"];

function ConfettiPiece({ index }: { index: number }) {
  const seed = index * 9301 + 49297;
  const rand = (n: number) => ((seed * (n + 1)) % 1000) / 1000;
  const left = rand(1) * 100;
  const delay = rand(2) * 1.2;
  const duration = 2.6 + rand(3) * 2.4;
  const size = 6 + rand(4) * 8;
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const rotate = rand(5) * 360;
  const sway = (rand(6) - 0.5) * 120;
  return (
    <span
      className="confetti-piece"
      style={{
        left: `${left}%`,
        width: size,
        height: size * 0.4,
        background: color,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        ["--rot" as any]: `${rotate}deg`,
        ["--sway" as any]: `${sway}px`,
      }}
    />
  );
}

export function PhaseCelebration({ phase, onDismiss, totalPhases }: PhaseCelebrationProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (!phase || reduceMotion) {
      setShowConfetti(false);
      return;
    }
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 7000);
    return () => clearTimeout(t);
  }, [phase?.id, reduceMotion]);

  const confettiPieces = useMemo(
    () => Array.from({ length: 90 }, (_, i) => <ConfettiPiece key={i} index={i} />),
    [phase?.id],
  );

  if (!phase) return null;

  const isFinalPhase = phase.id === totalPhases;

  return (
    <Dialog open={!!phase} onOpenChange={(open) => { if (!open) onDismiss(); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-orange-500/30 bg-gradient-to-b from-[#1a0f08] to-[#0a0e16]"
        data-testid="dialog-phase-celebration"
      >
        <VisuallyHidden>
          <DialogTitle>Phase {phase.id} complete: {phase.name}</DialogTitle>
          <DialogDescription>{phase.hype}</DialogDescription>
        </VisuallyHidden>
        <style>{`
          @keyframes confetti-fall {
            0%   { transform: translate3d(0, -20px, 0) rotate(0deg); opacity: 1; }
            85%  { opacity: 1; }
            100% { transform: translate3d(var(--sway, 0), 110vh, 0) rotate(var(--rot, 360deg)); opacity: 0; }
          }
          .confetti-piece {
            position: absolute;
            top: -10%;
            border-radius: 2px;
            animation-name: confetti-fall;
            animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
            animation-iteration-count: 1;
            animation-fill-mode: forwards;
            will-change: transform, opacity;
            pointer-events: none;
          }
          @keyframes trophy-pop {
            0%   { transform: scale(0.3) rotate(-12deg); opacity: 0; }
            55%  { transform: scale(1.15) rotate(6deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
          .trophy-pop { animation: trophy-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
          @keyframes ring-glow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.45); }
            50%      { box-shadow: 0 0 40px 12px rgba(249, 115, 22, 0.0); }
          }
          .ring-glow { animation: ring-glow 2.4s ease-out infinite; }
        `}</style>

        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none overflow-hidden z-[60]" aria-hidden>
            {confettiPieces}
          </div>
        )}

        <div className="relative px-6 pt-8 pb-6 text-center">
          <div className="flex justify-center mb-4">
            <div
              className="trophy-pop ring-glow w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, rgba(249,115,22,0.25), rgba(251,191,36,0.15))",
                border: "2px solid rgba(249,115,22,0.5)",
              }}
            >
              <Trophy className="w-10 h-10 text-orange-400" strokeWidth={1.8} />
            </div>
          </div>

          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-400 mb-2 flex items-center justify-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Phase {phase.id} of {totalPhases} Complete
          </div>

          <h2
            className="text-2xl font-bold text-white mb-1"
            data-testid="text-celebration-phase-name"
          >
            {phase.emoji} {phase.name} unlocked
          </h2>
          <p className="text-sm text-muted-foreground mb-5">{phase.subtitle}</p>

          <div
            className="rounded-lg px-4 py-3 mb-5 text-left flex gap-3"
            style={{
              background: "rgba(249, 115, 22, 0.06)",
              border: "1px solid rgba(249, 115, 22, 0.2)",
            }}
          >
            <TrendingUp className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
            <p className="text-sm text-white/85 leading-relaxed">{phase.hype}</p>
          </div>

          {isFinalPhase ? (
            <p className="text-xs text-emerald-400 font-medium mb-4">
              All four phases complete — your GBP is fully fortified. Time to dominate. \uD83D\uDC51
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mb-4">
              Next up: <span className="text-white font-semibold">Phase {phase.id + 1}</span>. Keep
              the momentum going.
            </p>
          )}

          <Button
            className="w-full text-white font-semibold"
            style={{ background: "#f97316" }}
            onClick={onDismiss}
            data-testid="button-celebration-continue"
          >
            Let's keep dominating
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
